import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Tag,
  Printer,
  Search,
  Plus,
  CheckCircle,
  Ban,
  Lock,
  ExternalLink,
  Hash,
  ChevronDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  abrirPestana,
  listarStickers,
  generarStickers,
  imprimirStickers,
  imprimirSticker,
  anularSticker,
  urlCertificadoPublico,
  obtenerConfigStickers,
  guardarConfigStickers,
  previsualizarNumero,
} from "../../utils/calibStickers";

const PAGE_SIZE = 60;
const CANTIDADES_RAPIDAS = [10, 25, 50, 100];

const TABS = [
  { key: "libre", label: "Libres", icon: Tag, color: "blue" },
  { key: "asignado", label: "Asignados", icon: CheckCircle, color: "green" },
  { key: "anulado", label: "Anulados", icon: Ban, color: "gray" },
];

const TAB_COLORS = {
  blue: "border-blue-500 bg-blue-50 text-blue-700",
  green: "border-green-500 bg-green-50 text-green-700",
  gray: "border-gray-400 bg-gray-100 text-gray-700",
};

const formatoFecha = (f) =>
  f ? new Date(f).toLocaleDateString("es-CO") : "—";

// Fecha local en AAAA-MM-DD (valor del input type="date")
const hoyISO = () => new Date().toLocaleDateString("en-CA");
const fechaLegible = (iso) => iso.split("-").reverse().join("/");

const FORMATOS = [
  { key: "rollo", label: "Rollo 50×25 mm" },
  { key: "carta", label: "Hoja carta" },
];

const SelectorFormato = ({ formato, onChange }) => (
  <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 mt-1">
    {FORMATOS.map((f) => (
      <button
        key={f.key}
        type="button"
        onClick={() => onChange(f.key)}
        className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
          formato === f.key
            ? "bg-blue-600 text-white"
            : "text-gray-600 hover:bg-gray-50"
        }`}
      >
        {f.label}
      </button>
    ))}
  </div>
);

// ─── Switch de fecha del sticker (generar y reimprimir) ──────────────────
const OpcionFecha = ({ activo, onActivo, fecha, onFecha, children }) => (
  <div>
    <span className="text-xs text-gray-600">Fecha de calibración</span>
    <div className="flex flex-wrap items-center gap-2 mt-1 min-h-[38px]">
      <button
        type="button"
        role="switch"
        aria-checked={activo}
        aria-label="Imprimir fecha en el sticker"
        onClick={() => onActivo(!activo)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          activo ? "bg-blue-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            activo ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
      <button
        type="button"
        onClick={() => onActivo(!activo)}
        className="text-sm text-gray-700 select-none"
      >
        {activo ? "Con fecha" : "Sin fecha"}
      </button>
      {activo && !children && (
        <input
          type="date"
          value={fecha}
          onChange={(e) => onFecha(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-1 focus:ring-blue-400 outline-none"
        />
      )}
    </div>
    {activo && children}
  </div>
);

const AyudaFecha = ({ fecha }) =>
  fecha ? (
    <>
      Se imprimirá la fecha{" "}
      <span className="font-semibold text-gray-800">{fechaLegible(fecha)}</span>.
    </>
  ) : (
    <>La fecha queda en blanco (__/__/____) para diligenciarla a mano.</>
  );

// ─── Reimprimir seleccionados ────────────────────────────────────────────
const ReimprimirModal = ({ ids, asignados, formatoInicial, onCerrar }) => {
  const [formato, setFormato] = useState(formatoInicial);
  // Los asignados ya tienen fecha de calibración: por defecto se conserva
  const [conFecha, setConFecha] = useState(asignados);
  const [usarRegistrada, setUsarRegistrada] = useState(asignados);
  const [fecha, setFecha] = useState(hoyISO());

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCerrar();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCerrar]);

  const fechaManual = conFecha && !usarRegistrada;

  const handleImprimir = () => {
    if (fechaManual && !fecha) {
      toast.error("Selecciona la fecha a imprimir");
      return;
    }
    imprimirStickers({
      ids,
      formato,
      fecha: !conFecha ? "sin" : usarRegistrada ? undefined : fecha,
    });
    onCerrar();
  };

  const pillClass = (activa) =>
    `flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
      activa ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"
    }`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4"
      onClick={onCerrar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reimprimir-titulo"
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-5 border-b">
          <h3
            id="reimprimir-titulo"
            className="text-base font-semibold text-gray-900 flex items-center gap-2"
          >
            <Printer size={17} className="text-blue-600" />
            Imprimir {ids.length} sticker{ids.length === 1 ? "" : "s"}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Elige el formato y si el sticker lleva fecha impresa. La fecha
            solo aplica a esta impresión.
          </p>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          <div>
            <span className="text-xs text-gray-600">Formato de impresión</span>
            <SelectorFormato formato={formato} onChange={setFormato} />
          </div>

          <OpcionFecha
            activo={conFecha}
            onActivo={setConFecha}
            fecha={fecha}
            onFecha={setFecha}
          >
            {asignados && (
              <div className="mt-2 space-y-2">
                <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
                  <button
                    type="button"
                    onClick={() => setUsarRegistrada(true)}
                    className={pillClass(usarRegistrada)}
                  >
                    La de calibración
                  </button>
                  <button
                    type="button"
                    onClick={() => setUsarRegistrada(false)}
                    className={pillClass(!usarRegistrada)}
                  >
                    Otra fecha
                  </button>
                </div>
                {!usarRegistrada && (
                  <input
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-blue-400 outline-none"
                  />
                )}
              </div>
            )}
            {!asignados && (
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="mt-2 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-blue-400 outline-none"
              />
            )}
          </OpcionFecha>

          <p className="text-xs text-gray-500 rounded-lg bg-gray-50 border border-gray-100 p-2.5">
            {conFecha && usarRegistrada ? (
              <>Cada sticker se imprime con la fecha en que fue calibrado.</>
            ) : (
              <AyudaFecha fecha={fechaManual ? fecha : ""} />
            )}
          </p>
        </div>

        <div className="p-4 sm:p-5 border-t flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleImprimir}
          >
            <Printer size={15} className="mr-1.5" />
            Imprimir
          </Button>
        </div>
      </div>
    </div>
  );
};

const COMODINES = [
  { token: "{AAAA}", label: "Año (2026)" },
  { token: "{AA}", label: "Año (26)" },
  { token: "{MM}", label: "Mes (01-12)" },
];

// ─── Nomenclatura y consecutivo del certificado ──────────────────────────
const NomenclaturaCertificado = ({ config, onGuardado }) => {
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [campoActivo, setCampoActivo] = useState("prefijo");

  useEffect(() => {
    if (config) setForm({ ...config });
  }, [config]);

  if (!form) return null;

  const cambiar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const sinCambios =
    form.prefijo === config.prefijo &&
    form.sufijo === config.sufijo &&
    Number(form.digitos) === config.digitos &&
    Number(form.siguiente) === config.siguiente;

  const insertarComodin = (token) =>
    cambiar(campoActivo, `${form[campoActivo] || ""}${token}`);

  const handleGuardar = async () => {
    if (Number(form.siguiente) < config.siguiente) {
      const ok = window.confirm(
        `El próximo consecutivo (${form.siguiente}) es menor al actual (${config.siguiente}). ` +
          "Si esos números ya existen con esta nomenclatura, no se podrán generar. ¿Continuar?",
      );
      if (!ok) return;
    }
    setGuardando(true);
    try {
      const res = await guardarConfigStickers({
        prefijo: form.prefijo,
        sufijo: form.sufijo,
        digitos: Number(form.digitos),
        siguiente: Number(form.siguiente),
      });
      toast.success(res.message);
      onGuardado(res);
      setAbierto(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const inputClass =
    "w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-blue-400 outline-none";

  return (
    <section className="rounded-xl border border-gray-200">
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <Hash size={16} className="text-blue-600" />
          Nomenclatura y consecutivo del certificado
        </span>
        <span className="flex items-center gap-2 text-xs text-gray-500">
          Próximo número:
          <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
            {config.ejemplo}
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform ${abierto ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {abierto && (
        <div className="border-t p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-600">Prefijo</label>
              <input
                className={`${inputClass} font-mono mt-1`}
                value={form.prefijo}
                maxLength={20}
                onFocus={() => setCampoActivo("prefijo")}
                onChange={(e) => cambiar("prefijo", e.target.value)}
                placeholder="CAL-"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Dígitos del consecutivo</label>
              <input
                type="number"
                min={1}
                max={10}
                className={`${inputClass} mt-1`}
                value={form.digitos}
                onChange={(e) => cambiar("digitos", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Sufijo (opcional)</label>
              <input
                className={`${inputClass} font-mono mt-1`}
                value={form.sufijo}
                maxLength={20}
                onFocus={() => setCampoActivo("sufijo")}
                onChange={(e) => cambiar("sufijo", e.target.value)}
                placeholder="-{AAAA}"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Próximo consecutivo</label>
              <input
                type="number"
                min={1}
                className={`${inputClass} mt-1`}
                value={form.siguiente}
                onChange={(e) => cambiar("siguiente", e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            Insertar en {campoActivo === "prefijo" ? "prefijo" : "sufijo"}:
            {COMODINES.map((c) => (
              <button
                key={c.token}
                type="button"
                onClick={() => insertarComodin(c.token)}
                className="px-2 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 font-mono text-gray-700"
                title={c.label}
              >
                {c.token}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg bg-blue-50 border border-blue-100 p-3">
            <div className="text-xs text-gray-600">
              Vista previa del próximo certificado
              <div className="font-mono text-lg font-bold text-blue-800 break-all">
                {previsualizarNumero(form)}
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                className="bg-white"
                disabled={guardando}
                onClick={() => {
                  setForm({ ...config });
                  setAbierto(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={guardando || sinCambios}
                onClick={handleGuardar}
              >
                {guardando ? "Guardando…" : "Guardar nomenclatura"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Aplica a los stickers que se generen desde ahora; los ya generados
            conservan su número.
          </p>
        </div>
      )}
    </section>
  );
};

const CalibStickersPage = () => {
  const [estado, setEstado] = useState("libre");
  const [busqueda, setBusqueda] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [conteos, setConteos] = useState({ libre: 0, asignado: 0, anulado: 0 });
  const [cargando, setCargando] = useState(false);
  const [seleccion, setSeleccion] = useState([]);

  const [cantidad, setCantidad] = useState(25);
  const [formato, setFormato] = useState("rollo");
  const [conFecha, setConFecha] = useState(false);
  const [fechaGen, setFechaGen] = useState(hoyISO());
  const [generando, setGenerando] = useState(false);
  const [config, setConfig] = useState(null);
  const [reimprimir, setReimprimir] = useState(false);
  const cerrarReimprimir = useCallback(() => setReimprimir(false), []);

  const cargarConfig = useCallback(() => {
    obtenerConfigStickers()
      .then(setConfig)
      .catch(() => toast.error("Error al cargar la nomenclatura"));
  }, []);

  useEffect(() => {
    cargarConfig();
  }, [cargarConfig]);

  const cargar = useCallback(
    async (pagina = 1) => {
      setCargando(true);
      try {
        const data = await listarStickers({
          estado,
          q: busqueda || undefined,
          page: pagina,
          limit: PAGE_SIZE,
        });
        setItems((prev) => (pagina === 1 ? data.items : [...prev, ...data.items]));
        setTotal(data.total);
        setConteos(data.conteos);
        setPage(pagina);
      } catch {
        toast.error("Error al cargar los stickers");
      } finally {
        setCargando(false);
      }
    },
    [estado, busqueda],
  );

  useEffect(() => {
    setSeleccion([]);
    const t = setTimeout(() => cargar(1), 250);
    return () => clearTimeout(t);
  }, [cargar]);

  const handleGenerar = async () => {
    const n = parseInt(cantidad, 10);
    if (!n || n < 1 || n > 500) {
      toast.error("Ingresa una cantidad entre 1 y 500");
      return;
    }
    if (conFecha && !fechaGen) {
      toast.error("Selecciona la fecha a imprimir o desactiva la fecha");
      return;
    }
    // Abrir la pestaña dentro del clic para que no se bloquee como emergente
    const win = abrirPestana();
    setGenerando(true);
    try {
      const res = await generarStickers(n);
      toast.success(`Generados ${res.desde} a ${res.hasta}`);
      imprimirStickers(
        { lote: res.lote, formato, fecha: conFecha ? fechaGen : undefined },
        win,
      );
      setEstado("libre");
      cargar(1);
      cargarConfig();
    } catch (err) {
      win?.close();
      toast.error(err.response?.data?.message || "Error al generar stickers");
    } finally {
      setGenerando(false);
    }
  };

  const toggle = (id) =>
    setSeleccion((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const todosSeleccionados =
    items.length > 0 && items.every((s) => seleccion.includes(s._id));

  const handleAccion = async (fn, s, confirmacion) => {
    if (!window.confirm(confirmacion)) return;
    try {
      const res = await fn(s._id);
      toast.success(res.message);
      setSeleccion((prev) => prev.filter((x) => x !== s._id));
      cargar(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo completar la acción");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Stickers de Calibración
        </h2>
        <p className="text-sm text-gray-500">
          Genera e imprime stickers antes de la visita. Al finalizar cada
          calibración, el técnico asigna el sticker que pega en el equipo; su
          número es el número del certificado y su QR abre el certificado sin
          necesidad de iniciar sesión.
        </p>
      </div>

      <NomenclaturaCertificado config={config} onGuardado={setConfig} />

      {/* ── Generar ── */}
      <section className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Plus size={16} className="text-blue-600" /> Generar stickers
        </h3>
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <label className="text-xs text-gray-600">Cantidad</label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <input
                type="number"
                min={1}
                max={500}
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm w-24 bg-white focus:ring-1 focus:ring-blue-400 outline-none"
              />
              {CANTIDADES_RAPIDAS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCantidad(n)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    Number(cantidad) === n
                      ? "border-blue-500 bg-blue-600 text-white"
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-600">Formato de impresión</span>
            <SelectorFormato formato={formato} onChange={setFormato} />
          </div>
          <OpcionFecha
            activo={conFecha}
            onActivo={setConFecha}
            fecha={fechaGen}
            onFecha={setFechaGen}
          />
          <Button
            type="button"
            onClick={handleGenerar}
            disabled={generando}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Printer size={15} className="mr-1.5" />
            {generando ? "Generando…" : "Generar e imprimir"}
          </Button>
        </div>
        {config && parseInt(cantidad, 10) > 0 && (
          <p className="text-xs text-gray-500 mt-3">
            Se generarán del{" "}
            <span className="font-mono font-semibold text-gray-800">
              {config.ejemplo}
            </span>
            {parseInt(cantidad, 10) > 1 && (
              <>
                {" "}al{" "}
                <span className="font-mono font-semibold text-gray-800">
                  {previsualizarNumero({
                    ...config,
                    siguiente: config.siguiente + parseInt(cantidad, 10) - 1,
                  })}
                </span>
              </>
            )}
            . <AyudaFecha fecha={conFecha ? fechaGen : ""} />
          </p>
        )}
      </section>

      {/* ── Pestañas por estado ── */}
      <div className="grid grid-cols-3 gap-2">
        {TABS.map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            type="button"
            onClick={() => setEstado(key)}
            className={`rounded-xl border-2 p-3 text-left transition-colors ${
              estado === key
                ? TAB_COLORS[color]
                : "border-gray-100 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-1.5 text-xs font-medium">
              <Icon size={14} /> {label}
            </div>
            <div className="text-2xl font-bold mt-0.5">{conteos[key] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* ── Barra de herramientas ── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por número, ej. 000123"
            className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
          />
        </div>
        {estado !== "anulado" && (
          <Button
            type="button"
            variant="outline"
            disabled={!seleccion.length}
            onClick={() => setReimprimir(true)}
          >
            <Printer size={15} className="mr-1.5" />
            Imprimir seleccionados ({seleccion.length})
          </Button>
        )}
      </div>

      {/* ── Listado ── */}
      {items.length > 0 && estado !== "anulado" && (
        <label className="flex items-center gap-2 text-xs text-gray-600 select-none">
          <input
            type="checkbox"
            checked={todosSeleccionados}
            onChange={() =>
              setSeleccion(todosSeleccionados ? [] : items.map((s) => s._id))
            }
          />
          Seleccionar los {items.length} visibles
        </label>
      )}

      {items.length === 0 && !cargando ? (
        <div className="text-center py-10 text-sm text-gray-500 border border-dashed rounded-xl">
          {estado === "libre"
            ? "No hay stickers libres. Genera un lote para llevarlos impresos a la visita."
            : estado === "asignado"
              ? "Aún no se han asignado stickers a calibraciones."
              : "No hay stickers anulados."}
        </div>
      ) : estado === "libre" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
          {items.map((s) => (
            <div
              key={s._id}
              className={`group flex items-center gap-2 border rounded-lg px-2.5 py-2 transition-colors ${
                seleccion.includes(s._id)
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                checked={seleccion.includes(s._id)}
                onChange={() => toggle(s._id)}
              />
              <button
                type="button"
                onClick={() => toggle(s._id)}
                className="flex-1 text-left font-mono text-sm text-gray-800 truncate"
              >
                {s.numero}
              </button>
              <button
                type="button"
                title="Imprimir"
                onClick={() => imprimirSticker(s._id, formato)}
                className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
              >
                <Printer size={14} />
              </button>
              <button
                type="button"
                title="Anular (perdido o dañado)"
                onClick={() =>
                  handleAccion(
                    anularSticker,
                    s,
                    `¿Anular el sticker ${s.numero}? No podrá asignarse a ningún servicio.`,
                  )
                }
                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
              >
                <Ban size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="divide-y border rounded-xl">
          {items.map((s) => (
            <div
              key={s._id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 py-2.5"
            >
              <div className="flex items-center gap-3 sm:w-44">
                {estado === "asignado" && (
                  <input
                    type="checkbox"
                    checked={seleccion.includes(s._id)}
                    onChange={() => toggle(s._id)}
                  />
                )}
                <span className="font-mono text-sm font-semibold text-gray-800">
                  {s.numero}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-xs text-gray-600">
                {estado === "asignado" ? (
                  <>
                    <div className="truncate text-gray-800">
                      {s.equipo?.categoria?.name || "Equipo"} ·{" "}
                      {s.equipo?.codigo || "—"} · Serial {s.equipo?.serial || "—"}
                    </div>
                    <div className="truncate">
                      {s.equipo?.cliente?.name || "—"} · Calibrado{" "}
                      {formatoFecha(s.fechaCalibracion)}
                      {s.asignadoPor?.name ? ` · ${s.asignadoPor.name}` : ""}
                    </div>
                  </>
                ) : (
                  <span>Anulado el {formatoFecha(s.updatedAt)}</span>
                )}
              </div>
              {estado === "asignado" && (
                <div className="flex gap-1 self-end sm:self-auto">
                  <button
                    type="button"
                    title="Ver certificado público"
                    onClick={() =>
                      window.open(urlCertificadoPublico(s.token), "_blank")
                    }
                    className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                  >
                    <ExternalLink size={15} />
                  </button>
                  <button
                    type="button"
                    title="Imprimir"
                    onClick={() => imprimirSticker(s._id, formato)}
                    className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                  >
                    <Printer size={15} />
                  </button>
                  <span
                    title="Asignado: no puede modificarse"
                    className="p-1.5 text-gray-300"
                  >
                    <Lock size={15} />
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {cargando && (
        <p className="text-center text-xs text-gray-400">Cargando…</p>
      )}
      {!cargando && items.length < total && (
        <div className="text-center">
          <Button type="button" variant="outline" onClick={() => cargar(page + 1)}>
            Cargar más ({total - items.length} restantes)
          </Button>
        </div>
      )}

      {reimprimir && (
        <ReimprimirModal
          ids={seleccion}
          asignados={estado === "asignado"}
          formatoInicial={formato}
          onCerrar={cerrarReimprimir}
        />
      )}
    </div>
  );
};

export default CalibStickersPage;
